import {
	aggregateDotsByMonthSum,
	isDateRangeLongerThanMonths,
} from 'components/shared/processes/chartDateAggregation'
import { useMilkingProcessGraph } from 'hook/query/useGetMilkingProcessGraph'
import { ShiftOptions } from 'models/ICattlesData'
import { IMilkingProcessGraphDot } from 'models/MilkingProcess'
import moment from 'moment'
import { useEffect, useMemo, useState } from 'react'
import { useAppSelector } from 'store/hooks'

type ShiftEntry = { am: number | null; pm: number | null; night: number | null }

export type ShiftCountsByDate = Record<string, ShiftEntry>
export type ShiftCountsByTimestamp = Record<number, ShiftEntry>

interface UseDeviationChartDataParams {
	startDate: number
	endDate: number
	daysBetweenDates: number
	shift: ShiftOptions[] | 'all'
	parlorId: number[] | 'all'
}

// --- pure helpers ---

const emptyEntry = (): ShiftEntry => ({ am: null, pm: null, night: null })

const addShiftDots = (
	map: ShiftCountsByDate,
	dots: IMilkingProcessGraphDot[] | undefined,
	field: 'am' | 'pm' | 'night',
) => {
	dots?.forEach(dot => {
		const key = moment(dot.timestamp).format('YYYY-MM-DD')
		if (!map[key]) map[key] = emptyEntry()
		map[key][field] = (map[key][field] ?? 0) + (dot.count ?? 0)
	})
}

const HOUR_MS = 60 * 60 * 1000
const toHour = (ts: number) => Math.floor(ts / HOUR_MS) * HOUR_MS
const ceilToHour = (ts: number) => Math.ceil(ts / HOUR_MS) * HOUR_MS

const addShiftDotsByTimestamp = (
	map: ShiftCountsByTimestamp,
	dots: IMilkingProcessGraphDot[] | undefined,
	field: 'am' | 'pm' | 'night',
) => {
	dots?.forEach(dot => {
		const key = toHour(dot.timestamp)
		if (!map[key]) map[key] = emptyEntry()
		map[key][field] = (map[key][field] ?? 0) + (dot.count ?? 0)
	})
}

const aggregateShiftCountsByMonth = (
	map: ShiftCountsByDate,
): ShiftCountsByDate => {
	const result: ShiftCountsByDate = {}

	Object.entries(map).forEach(([dateKey, entry]) => {
		const monthKey = moment(dateKey).startOf('month').format('YYYY-MM-DD')
		if (!result[monthKey]) result[monthKey] = emptyEntry()
		;(['am', 'pm', 'night'] as const).forEach(field => {
			if (entry[field] == null) return
			result[monthKey][field] = (result[monthKey][field] ?? 0) + entry[field]
		})
	})

	return result
}

const buildShiftHourlySeries = (
	startTs: number,
	endTs: number,
	categoryPadValue: number | null = null,
) => {
	const timestamp: number[] = []
	const categories: (number | null)[] = []
	const interpolated: (number | null)[] = []
	let current = startTs

	while (current <= endTs) {
		timestamp.push(current)
		categories.push(categoryPadValue)
		interpolated.push(null)
		current = moment(current).add(1, 'hour').valueOf()
	}

	return { timestamp, categories, interpolated }
}

const mapDotsToShiftHourlySeries = (
	dots: IMilkingProcessGraphDot[],
	startTs: number,
	endTs: number,
	categoryPadValue: number | null = null,
) => {
	const dotByHour = new Map<number, number | null>()
	dots.forEach(dot => {
		dotByHour.set(moment(dot.timestamp).startOf('hour').valueOf(), dot.count)
	})

	const timestamp: number[] = []
	const categories: (number | null)[] = []
	let current = startTs

	while (current <= endTs) {
		timestamp.push(current)
		categories.push(
			dotByHour.has(current) ? dotByHour.get(current)! : categoryPadValue,
		)
		current = moment(current).add(1, 'hour').valueOf()
	}

	return {
		timestamp,
		categories,
		interpolated: buildInterpolated(categories),
	}
}

const buildPlaceholderSeries = (
	startDate: number,
	endDate: number,
	daysBetweenDates: number,
) => {
	const waveValues = [6, 16, 6, 16, 6]
	const step = Math.floor((endDate - startDate) / (waveValues.length - 1))
	const hourMs = 60 * 60 * 1000
	const timestamp =
		daysBetweenDates === 0
			? [0, 6, 12, 18, 24].map(h =>
					moment(startDate).startOf('day').add(h, 'hours').valueOf(),
				)
			: waveValues.map(
					(_, i) => Math.round((startDate + step * i) / hourMs) * hourMs,
				)
	return {
		timestamp,
		categories: new Array(waveValues.length).fill(null) as null[],
		interpolated: waveValues,
	}
}

const buildInterpolated = (categories: (number | null)[]) => {
	const result: (number | null)[] = new Array(categories.length).fill(null)
	for (let i = 0; i < categories.length; i++) {
		if (categories[i] !== null) continue

		let start = i
		while (start > 0 && categories[start - 1] === null) start--
		let end = i
		while (end < categories.length - 1 && categories[end + 1] === null) end++

		const prev = categories[start - 1]
		const next = categories[end + 1]

		if (typeof prev === 'number' && typeof next === 'number') {
			const totalSteps = end - start + 2
			result[start - 1] = prev
			for (let j = start; j <= end; j++)
				result[j] = prev + ((next - prev) / totalSteps) * (j - start + 1)
			result[end + 1] = next
		} else if (typeof prev === 'number') {
			result[start - 1] = prev
			for (let j = start; j <= end; j++) result[j] = prev
		} else if (typeof next === 'number') {
			for (let j = start; j <= end; j++) result[j] = next
			result[end + 1] = next
		}
		i = end + 1
	}
	return result
}

const trimSeriesByEnd = (
	timestamp: number[],
	categories: (number | null)[],
	interpolated: (number | null)[],
	endTs?: number,
) => {
	if (!endTs || timestamp.length === 0) {
		return { timestamp, categories, interpolated }
	}

	let visibleLength = 0
	while (
		visibleLength < timestamp.length &&
		timestamp[visibleLength] <= endTs
	) {
		visibleLength++
	}

	if (visibleLength === 0) {
		return {
			timestamp: timestamp.slice(0, 1),
			categories: categories.slice(0, 1),
			interpolated: interpolated.slice(0, 1),
		}
	}

	return {
		timestamp: timestamp.slice(0, visibleLength),
		categories: categories.slice(0, visibleLength),
		interpolated: interpolated.slice(0, visibleLength),
	}
}

const trimSeriesByStart = (
	timestamp: number[],
	categories: (number | null)[],
	interpolated: (number | null)[],
	startTs?: number,
) => {
	if (!startTs || timestamp.length === 0) {
		return { timestamp, categories, interpolated }
	}

	let visibleStartIndex = 0
	while (
		visibleStartIndex < timestamp.length &&
		timestamp[visibleStartIndex] < startTs
	) {
		visibleStartIndex++
	}

	if (visibleStartIndex >= timestamp.length) {
		return {
			timestamp: timestamp.slice(-1),
			categories: categories.slice(-1),
			interpolated: interpolated.slice(-1),
		}
	}

	return {
		timestamp: timestamp.slice(visibleStartIndex),
		categories: categories.slice(visibleStartIndex),
		interpolated: interpolated.slice(visibleStartIndex),
	}
}

const parseTimeToHourBound = (
	dayStartTs: number,
	timeValue?: string,
	roundMode: 'floor' | 'ceil' = 'floor',
) => {
	if (!timeValue) return null
	const [hoursRaw, minutesRaw = '0', secondsRaw = '0'] = timeValue.split(':')
	const hours = Number(hoursRaw)
	const minutes = Number(minutesRaw)
	const seconds = Number(secondsRaw)

	if (
		Number.isNaN(hours) ||
		Number.isNaN(minutes) ||
		Number.isNaN(seconds) ||
		hours < 0 ||
		hours > 23
	) {
		return null
	}

	const ts = moment(dayStartTs)
		.hour(hours)
		.minute(minutes)
		.second(seconds)
		.millisecond(0)
		.valueOf()

	if (roundMode === 'ceil') {
		const withMinutesOrSeconds = minutes > 0 || seconds > 0
		return withMinutesOrSeconds
			? moment(ts).add(1, 'hour').startOf('hour').valueOf()
			: ts
	}

	return moment(ts).startOf('hour').valueOf()
}

const extendSeriesToHourBound = (
	timestamp: number[],
	categories: (number | null)[],
	interpolated: (number | null)[],
	endTs?: number | null,
	categoryPadValue: number | null = null,
) => {
	if (!endTs || timestamp.length === 0) {
		return { timestamp, categories, interpolated }
	}

	const extendedTimestamp = [...timestamp]
	const extendedCategories = [...categories]
	const extendedInterpolated = [...interpolated]
	let currentLastTs = extendedTimestamp[extendedTimestamp.length - 1]

	while (currentLastTs < endTs) {
		currentLastTs += HOUR_MS
		extendedTimestamp.push(currentLastTs)
		extendedCategories.push(categoryPadValue)
		extendedInterpolated.push(null)
	}

	return {
		timestamp: extendedTimestamp,
		categories: extendedCategories,
		interpolated: extendedInterpolated,
	}
}

const buildMockSeries = (startDate: number, endDate: number) => {
	const startHour = moment(startDate).startOf('hour').valueOf()
	const endHour = moment(endDate).startOf('hour').valueOf()
	const timestamp: number[] = []
	const categories: (number | null)[] = []
	let current = startHour
	let index = 0

	while (current <= endHour) {
		timestamp.push(current)
		// Небольшая "живая" синтетика: волна + редкие нули.
		const value = Math.max(0, Math.round(6 + 7 * Math.sin(index / 2.3)))
		categories.push(index % 9 === 0 ? 0 : value)
		current += HOUR_MS
		index += 1
	}

	return {
		timestamp,
		categories,
		interpolated: new Array(timestamp.length).fill(null) as (number | null)[],
	}
}

// --- hook ---

export const useDeviationChartData = ({
	startDate,
	endDate,
	daysBetweenDates,
	shift,
	parlorId,
}: UseDeviationChartDataParams) => {
	const { userData } = useAppSelector(state => state.userReducer)
	const mockModeEnabled =
		typeof window !== 'undefined' &&
		window.localStorage.getItem('sf_mock_milking_deviation_chart') === '1'
	const [tickTs, setTickTs] = useState(() => Date.now())
	const dayDate =
		daysBetweenDates === 0 ? moment(startDate).format('YYYY-MM-DD') : undefined
	const isTodayRange =
		daysBetweenDates === 0 && moment(startDate).isSame(tickTs, 'day')
	const roundedCurrentHour = useMemo(() => ceilToHour(tickTs), [tickTs])
	// For today without specific shift: cap at current hour so future data is hidden.
	// When a specific shift is selected, shift end takes priority (see effectiveEndTs below).
	const visibleRangeEndTs = isTodayRange ? roundedCurrentHour : undefined
	const roundedShiftEndTs = useMemo(() => {
		// Extend series only for single-day view with specific shift(s) selected.
		// When 'all' is selected we trust the API range as-is to avoid
		// stretching past-date graphs beyond actual data.
		if (daysBetweenDates !== 0) return null
		if (shift === 'all') return null

		const shiftStartTimeMap: Record<ShiftOptions, string | undefined> = {
			[ShiftOptions.AM]: userData?.company?.am_period_start,
			[ShiftOptions.PM]: userData?.company?.pm_period_start,
			[ShiftOptions.NIGHT]: userData?.company?.night_period_start,
		}
		const shiftEndTimeMap: Record<ShiftOptions, string | undefined> = {
			[ShiftOptions.AM]: userData?.company?.am_period_end,
			[ShiftOptions.PM]: userData?.company?.pm_period_end,
			[ShiftOptions.NIGHT]: userData?.company?.night_period_end,
		}

		const dayStart = moment(startDate).startOf('day').valueOf()
		const roundedEnds = shift
			.map(shiftOption => {
				const startTs = parseTimeToHourBound(
					dayStart,
					shiftStartTimeMap[shiftOption],
					'ceil',
				)
				const endTs = parseTimeToHourBound(
					dayStart,
					shiftEndTimeMap[shiftOption],
					'ceil',
				)

				if (startTs === null || endTs === null) return null

				// Handle overnight shifts (e.g. 21:50 - 06:30).
				return endTs <= startTs ? endTs + 24 * HOUR_MS : endTs
			})
			.filter((value): value is number => typeof value === 'number')

		return roundedEnds.length > 0 ? Math.max(...roundedEnds) : null
	}, [
		daysBetweenDates,
		shift,
		startDate,
		userData?.company?.am_period_start,
		userData?.company?.am_period_end,
		userData?.company?.pm_period_start,
		userData?.company?.pm_period_end,
		userData?.company?.night_period_start,
		userData?.company?.night_period_end,
	])
	const roundedShiftStartTs = useMemo(() => {
		if (daysBetweenDates !== 0) return null

		const shiftStartTimeMap: Record<ShiftOptions, string | undefined> = {
			[ShiftOptions.AM]: userData?.company?.am_period_start,
			[ShiftOptions.PM]: userData?.company?.pm_period_start,
			[ShiftOptions.NIGHT]: userData?.company?.night_period_start,
		}
		const dayStart = moment(startDate).startOf('day').valueOf()
		const shiftsToUse =
			shift === 'all'
				? [ShiftOptions.AM, ShiftOptions.PM, ShiftOptions.NIGHT]
				: shift
		const roundedStarts = shiftsToUse
			.map(shiftOption =>
				parseTimeToHourBound(dayStart, shiftStartTimeMap[shiftOption], 'ceil'),
			)
			.filter((value): value is number => typeof value === 'number')

		return roundedStarts.length > 0 ? Math.min(...roundedStarts) : null
	}, [
		daysBetweenDates,
		shift,
		startDate,
		userData?.company?.am_period_start,
		userData?.company?.pm_period_start,
		userData?.company?.night_period_start,
	])
	const isSelectedDayInFuture =
		daysBetweenDates === 0 &&
		moment(startDate).startOf('day').isAfter(moment(tickTs).startOf('day'))
	const isShiftNotStarted =
		daysBetweenDates === 0 &&
		shift !== 'all' &&
		roundedShiftStartTs !== null &&
		(isSelectedDayInFuture ||
			(isTodayRange && roundedShiftStartTs > roundedCurrentHour))

	// For today: never extend past the current hour.
	// For past dates with a specific shift: use the shift end boundary.
	const shiftEndTs = roundedShiftEndTs ?? undefined
	const effectiveEndTs =
		isTodayRange && visibleRangeEndTs !== undefined
			? shiftEndTs !== undefined
				? Math.min(shiftEndTs, visibleRangeEndTs)
				: visibleRangeEndTs
			: shiftEndTs
	const effectiveStartTs =
		roundedShiftStartTs &&
		effectiveEndTs &&
		roundedShiftStartTs > effectiveEndTs
			? undefined
			: (roundedShiftStartTs ?? undefined)
	const boundaryPadValue =
		roundedShiftEndTs &&
		(!isTodayRange || roundedShiftEndTs <= roundedCurrentHour)
			? 0
			: null

	const {
		data,
		isLoading,
		refetch: refetchAll,
	} = useMilkingProcessGraph(startDate, endDate, shift, parlorId, dayDate)
	const { data: amData, refetch: refetchAm } = useMilkingProcessGraph(
		startDate,
		endDate,
		[ShiftOptions.AM],
		parlorId,
		dayDate,
	)
	const { data: pmData, refetch: refetchPm } = useMilkingProcessGraph(
		startDate,
		endDate,
		[ShiftOptions.PM],
		parlorId,
		dayDate,
	)
	const { data: nightData, refetch: refetchNight } = useMilkingProcessGraph(
		startDate,
		endDate,
		[ShiftOptions.NIGHT],
		parlorId,
		dayDate,
	)

	useEffect(() => {
		if (!isTodayRange) return

		const now = Date.now()
		const nextHourTs = moment(now).startOf('hour').add(1, 'hour').valueOf()
		const timeoutId = window.setTimeout(
			() => {
				setTickTs(Date.now())
			},
			Math.max(nextHourTs - now, 1000),
		)

		return () => window.clearTimeout(timeoutId)
	}, [isTodayRange, tickTs])

	useEffect(() => {
		if (!isTodayRange) return
		refetchAll()
		refetchAm()
		refetchPm()
		refetchNight()
	}, [
		isTodayRange,
		roundedCurrentHour,
		refetchAll,
		refetchAm,
		refetchPm,
		refetchNight,
	])

	const shouldAggregateMonthly = useMemo(
		() => isDateRangeLongerThanMonths({ startDate, endDate }, 3),
		[startDate, endDate],
	)

	const shiftCountsByDate = useMemo<ShiftCountsByDate>(() => {
		const map: ShiftCountsByDate = {}
		addShiftDots(map, amData?.dots, 'am')
		addShiftDots(map, pmData?.dots, 'pm')
		addShiftDots(map, nightData?.dots, 'night')
		return shouldAggregateMonthly ? aggregateShiftCountsByMonth(map) : map
	}, [amData, pmData, nightData, shouldAggregateMonthly])

	const shiftCountsByTimestamp = useMemo<ShiftCountsByTimestamp>(() => {
		const map: ShiftCountsByTimestamp = {}
		addShiftDotsByTimestamp(map, amData?.dots, 'am')
		addShiftDotsByTimestamp(map, pmData?.dots, 'pm')
		addShiftDotsByTimestamp(map, nightData?.dots, 'night')
		return map
	}, [amData, pmData, nightData])

	const hasRealDeviations =
		data?.dots?.some(el => typeof el.count === 'number' && el.count > 0) ??
		false
	const hasGraphData =
		data?.dots?.some(el => typeof el.count === 'number') ?? false

	const base = {
		milkingGraph: data,
		isLoading,
		shiftCountsByDate,
		shiftCountsByTimestamp,
		hasRealDeviations,
		hasGraphData,
	}

	if (mockModeEnabled) {
		const mock = buildMockSeries(startDate, endDate)
		const extended = extendSeriesToHourBound(
			mock.timestamp,
			mock.categories,
			mock.interpolated,
			effectiveEndTs,
			boundaryPadValue,
		)
		const startTrimmed = trimSeriesByStart(
			extended.timestamp,
			extended.categories,
			extended.interpolated,
			effectiveStartTs,
		)
		const trimmed = trimSeriesByEnd(
			startTrimmed.timestamp,
			startTrimmed.categories,
			startTrimmed.interpolated,
			effectiveEndTs,
		)
		return {
			...base,
			hasRealDeviations: true,
			timestamp: trimmed.timestamp,
			categories: trimmed.categories,
			interpolatedCategories: trimmed.interpolated,
		}
	}

	const isSingleDayShiftSelection =
		daysBetweenDates === 0 &&
		shift !== 'all' &&
		effectiveStartTs !== undefined &&
		effectiveEndTs !== undefined

	if (isShiftNotStarted) {
		const { timestamp, categories, interpolated } = buildPlaceholderSeries(
			startDate,
			endDate,
			daysBetweenDates,
		)
		return {
			...base,
			timestamp,
			categories,
			interpolatedCategories: interpolated,
		}
	}

	if (!hasGraphData) {
		// For single-day "all shifts" view without data, keep a full-day placeholder shape.
		// It should not collapse to a single point around the current hour.
		if (daysBetweenDates === 0 && shift === 'all') {
			const { timestamp, categories, interpolated } = buildPlaceholderSeries(
				startDate,
				endDate,
				daysBetweenDates,
			)
			return {
				...base,
				timestamp,
				categories,
				interpolatedCategories: interpolated,
			}
		}

		if (isSingleDayShiftSelection) {
			const { timestamp, categories, interpolated } = buildShiftHourlySeries(
				effectiveStartTs,
				effectiveEndTs,
				boundaryPadValue,
			)
			return {
				...base,
				timestamp,
				categories,
				interpolatedCategories: interpolated,
			}
		}

		const { timestamp, categories, interpolated } = buildPlaceholderSeries(
			startDate,
			endDate,
			daysBetweenDates,
		)
		const extended = extendSeriesToHourBound(
			timestamp,
			categories,
			interpolated,
			effectiveEndTs,
			boundaryPadValue,
		)
		const startTrimmed = trimSeriesByStart(
			extended.timestamp,
			extended.categories,
			extended.interpolated,
			effectiveStartTs,
		)
		const trimmed = trimSeriesByEnd(
			startTrimmed.timestamp,
			startTrimmed.categories,
			startTrimmed.interpolated,
			effectiveEndTs,
		)
		return {
			...base,
			timestamp: trimmed.timestamp,
			categories: trimmed.categories,
			interpolatedCategories: trimmed.interpolated,
		}
	}

	if (isSingleDayShiftSelection && data?.dots) {
		const mapped = mapDotsToShiftHourlySeries(
			data.dots,
			effectiveStartTs,
			effectiveEndTs,
			boundaryPadValue,
		)
		return {
			...base,
			categories: mapped.categories,
			timestamp: mapped.timestamp,
			interpolatedCategories: mapped.interpolated,
		}
	}

	if (shouldAggregateMonthly && data?.dots) {
		const aggregated = aggregateDotsByMonthSum(
			data.dots
				.filter(dot => Number.isFinite(dot.count))
				.map(dot => ({
					value: dot.count as number,
					date_ms: dot.timestamp,
				})),
		)

		return {
			...base,
			categories: aggregated.map(dot => dot.value),
			timestamp: aggregated.map(dot => dot.date_ms),
			interpolatedCategories: new Array(aggregated.length).fill(null),
		}
	}

	const categories = data?.dots.map(el => el.count) ?? []
	const timestamp = data?.dots.map(el => el.timestamp) ?? []
	const interpolatedCategories = buildInterpolated(categories)
	const extended = extendSeriesToHourBound(
		timestamp,
		categories,
		interpolatedCategories,
		effectiveEndTs,
		boundaryPadValue,
	)
	const startTrimmed = trimSeriesByStart(
		extended.timestamp,
		extended.categories,
		extended.interpolated,
		effectiveStartTs,
	)
	const trimmed = trimSeriesByEnd(
		startTrimmed.timestamp,
		startTrimmed.categories,
		startTrimmed.interpolated,
		effectiveEndTs,
	)

	return {
		...base,
		categories: trimmed.categories,
		timestamp: trimmed.timestamp,
		interpolatedCategories: trimmed.interpolated,
	}
}
